import React, { useEffect, useState } from 'react';
import {
  AusGreen,
  Button,
  ButtonWrapper,
  Card,
  Container,
  Form,
  IconButton,
  InfoCard,
  InfoDescription,
  InfoIconCircle,
  InfoTextWrapper,
  InfoTitle,
  Input,
  InputWrapper,
  LogoContainer,
  OtpInput,
  TagLine,
  TextButton,
  Title,
  TitleRow,
} from './Login.style';
import { useAuth } from '../../../hooks/AuthContext';
import { useNavigate } from 'react-router-dom';
import usaLoginLogo from '../../../assets/PNG/Usa_cowboys.png';
import ausLogingLogo from '../../../assets/PNG/AgManagerBackgroudImage.png';
import AgManagerLogo from '../../../assets/PNG/AGMLogoForest.png';
import iosQR from '../../../assets/PNG/app_scan_qr.png';
import androidQR from '../../../assets/PNG/app_scan_qr.png';
import {
  getResetPasswordConfirmationCode,
  RestPassword,
  verification,
  resendVerificationCode,
} from '../../../services/bioplus-services/auth';
import { ClosedEye, OpenEye } from '../../../Components/icons';
import {
  validateEmail,
  validatePassword,
  validateResetPassword,
} from '../../../helper/validationHelper';
import FreeUserPopUpMessage from './modals/freeUserPopUp';
import { Email } from '@mui/icons-material';

// localStorage key — must match the key used in userInvitationV2.jsx
const RETURN_URL_KEY = 'agmanager_invitation_returnUrl';

const loginSteps = {
  login: 'LOGIN',
  resetPassword: 'RESET_PASSWORD',
  verify: 'VERIFY',
};

export const getLoginLogoBasedOnEnv = (env) => {
  switch (env) {
    case 'development':
      return usaLoginLogo;
    case 'stage':
      return usaLoginLogo;
    case 'production':
      return usaLoginLogo;
    case 'au_development':
      return ausLogingLogo;
    case 'au_stage':
      return ausLogingLogo;
    case 'au_production':
      return ausLogingLogo;
    default:
      return ausLogingLogo;
  }
};

export const getSignupUrlBasedOnEnv = (env) => {
  switch (env) {
    case 'au_stage':
      return 'https://register-stage-au.agmanager.app/';
    case 'au_production':
      return 'https://register-au.agmanager.app/';
    case 'production':
      return 'https://register-usa.agmanager.app/';
    default:
      return 'https://register-stage-au.agmanager.app/';
  }
};

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formStep, setFormStep] = useState(loginSteps.login);
  const [getConfirmationCode, setGetConfirmationCode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState(''); // Store email for reset password flow
  const [verificationEmail, setVerificationEmail] = useState(''); // Store email for verification flow
  const [errorMessages, setErrorMessages] = useState({
    email: '',
    password: '',
  });

  const [isInvite] = useState(() => window.location.pathname.includes('/auth/invitations/'));

  const [loading, setLoading] = useState(false);
  const [freeUserPopUp, setFreUserPopUp] = useState(false);
  const [otpResent, setOtpResent] = useState(false);

  useEffect(() => {
    if (errorMessages.email) {
      const timeoutId = setTimeout(() => {
        setErrorMessages((prevMessages) => ({
          ...prevMessages,
          email: '',
        }));
      }, 4000);
      return () => clearTimeout(timeoutId);
    }
    if (errorMessages.password) {
      const timeoutId = setTimeout(() => {
        setErrorMessages((prevMessages) => ({
          ...prevMessages,
          password: '',
        }));
      }, 4000);
      return () => clearTimeout(timeoutId);
    }
  }, [errorMessages.email, errorMessages.password]);

  /**
   * After a successful login, check if the user was originally trying to
   * open an invitation link. If so, redirect them back to it.
   * Otherwise fall through — SecureComponent will render the app normally.
   */
  const handlePostLoginRedirect = () => {
    const returnUrl = localStorage.getItem(RETURN_URL_KEY);
    if (returnUrl) {
      localStorage.removeItem(RETURN_URL_KEY);
      navigate(returnUrl);
    }
    // No else needed — if no returnUrl, SecureComponent handles rendering the app
  };

  const handleSubmit = async (event) => {
    try {
      event.preventDefault();
      setLoading(true);
      const data = new FormData(event.currentTarget);
      const isEmailValid = validateEmail(data.get('email'));
      const passwordError = validatePassword(data.get('password'));

      setVerificationEmail(data.get('email'));

      if (!isEmailValid) {
        setErrorMessages((prevMessages) => ({
          ...prevMessages,
          email: 'Please enter a valid email address.',
        }));
        setLoading(false);
        return;
      }

      if (passwordError !== true) {
        setErrorMessages((prevMessages) => ({
          ...prevMessages,
          password: passwordError,
        }));
        setLoading(false);
        return;
      }

      const s = await login(data.get('email'), data.get('password'), isInvite);

      if (!s) {
        setLoading(false);
        localStorage.removeItem('session');
        setFreUserPopUp(true);
      } else {
        // Login succeeded — redirect back to invitation URL if one was saved
        handlePostLoginRedirect();
      }
    } catch (error) {
      setLoading(false);
      if (error?.message === 'Invalid username or password') {
        alert('Credentials not found. Please check your email and password.');
      } else if (error?.message === 'Member not confirmed') {
        alert('Email is not verified. Please verify your email, then try to log in.');
        // Email not verified — redirect to verification step
        // const data = new FormData(event.currentTarget);
        // setVerificationEmail(data.get('email'));
        setFormStep(loginSteps.verify);
      } else {
        alert(error?.message ?? 'An unexpected error occurred. Please try again.');
      }
    }
  };

  const handleVerification = async (event) => {
    try {
      event.preventDefault();
      setLoading(true);
      const data = new FormData(event.currentTarget);
      const email = data.get('email');
      const verificationCode = data.get('verificationCode');

      if (!email) {
        setErrorMessages((prevMessages) => ({
          ...prevMessages,
          email: 'Email is required.',
        }));
        setLoading(false);
        return;
      }

      if (!verificationCode || verificationCode.trim() === '') {
        setErrorMessages((prevMessages) => ({
          ...prevMessages,
          password: 'Verification code is required.',
        }));
        setLoading(false);
        return;
      }

      await verification(email, verificationCode);
      setLoading(false);
      alert('Email verified successfully. You can now log in.');
      setFormStep(loginSteps.login);
      setVerificationEmail('');
      setErrorMessages({ email: '', password: '' });
    } catch (error) {
      setLoading(false);

      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        password: 'Verification failed. Please check your code and try again.',
      }));
    }
  };

  const handleResendVerificationCode = async () => {
    try {
      if (!verificationEmail) {
        setErrorMessages((prevMessages) => ({
          ...prevMessages,
          email: 'Please provide a valid email ID.',
        }));
        return;
      }
      setOtpResent(true);
      await resendVerificationCode(verificationEmail);
      setOtpResent(false);
      alert('Verification code sent. Please check your email.');
    } catch (error) {
      setOtpResent(false);

      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        email: 'Failed to resend verification code. Please try again.',
      }));
    }
  };

  const handleResetPassword = async (e) => {
    try {
      e.preventDefault();
      setLoading(true);

      // Step 1: Get confirmation code
      if (!getConfirmationCode) {
        const data = new FormData(e.currentTarget);
        const email = data.get('email');
        const isEmailValid = validateEmail(email);

        if (!isEmailValid) {
          setErrorMessages((prevMessages) => ({
            ...prevMessages,
            email: 'Please enter a valid email address.',
          }));
          setLoading(false);
          return;
        }

        try {
          const confirmationCode = await getResetPasswordConfirmationCode(email);
          if (confirmationCode) {
            setResetEmail(email); // Store email for second step
            setLoading(false);
            setGetConfirmationCode(true);
            setErrorMessages({ email: '', password: '' }); // Clear any previous errors
          }
        } catch (error) {
          setLoading(false);
          setErrorMessages((prevMessages) => ({
            ...prevMessages,
            email: 'Failed to send confirmation code. Please try again.',
          }));
        }
      }
      // Step 2: Reset password with confirmation code
      else if (getConfirmationCode) {
        const data = new FormData(e.currentTarget);
        const password = data.get('password');
        const confirmationCode = data.get('confirmationCode');
        const passwordError = validateResetPassword(password);

        if (passwordError !== true) {
          setErrorMessages((prevMessages) => ({
            ...prevMessages,
            password: passwordError,
          }));
          setLoading(false);
          return;
        }

        if (!confirmationCode || confirmationCode.trim() === '') {
          setErrorMessages((prevMessages) => ({
            ...prevMessages,
            password: 'Confirmation code is required.',
          }));
          setLoading(false);
          return;
        }

        try {
          await RestPassword(resetEmail, password, confirmationCode);
          setGetConfirmationCode(false);
          setFormStep(loginSteps.login);
          setResetEmail('');
          alert('Password reset successful.');
          setLoading(false);
          setErrorMessages({ email: '', password: '' });
        } catch (error) {
          setLoading(false);
          setErrorMessages((prevMessages) => ({
            ...prevMessages,
            password: 'Failed to reset password. Please check your code and try again.',
          }));
        }
      }
    } catch (error) {
      setLoading(false);
      alert('Oops, an error occurred, please try again sometime.');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  const handleSignupClick = () => {
    const url = getSignupUrlBasedOnEnv(process.env.REACT_APP_ENV);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleBackToLogin = () => {
    setFormStep(loginSteps.login);
    setGetConfirmationCode(false);
    setResetEmail('');
    setVerificationEmail('');
    setErrorMessages({ email: '', password: '' });
    setShowPassword(false);
  };

  const mainLogo = getLoginLogoBasedOnEnv(process.env.REACT_APP_ENV);

  return (
    <Container logo={mainLogo}>
      <LogoContainer src={AgManagerLogo} alt="AgManager Logo" />

      {/* VERIFICATION STEP */}
      {formStep === loginSteps.verify ? (
        <Card>
          <TitleRow>
            <Title>Verify Your Email</Title>
            <TextButton
              onClick={handleResendVerificationCode}
              color="primary"
              style={{ marginBottom: '30px' }}
            >
              <span style={{ textDecoration: 'underline' }}>
                {otpResent ? 'Resending...' : 'Resend OTP'}
              </span>
            </TextButton>
          </TitleRow>

          <Form onSubmit={handleVerification}>
            <div className="input-container">
              <Input
                type="email"
                placeholder="Email"
                name="email"
                value={verificationEmail}
                onChange={(e) => setVerificationEmail(e.target.value)}
              />
              {errorMessages.email && (
                <div className="error-tooltip">
                  <span className="error-text-span">{errorMessages.email}</span>
                </div>
              )}
            </div>

            <InputWrapper>
              <div className="input-container">
                <OtpInput name="verificationCode" placeholder="Enter Your OTP" type="text" />
                {errorMessages.password && (
                  <div className="error-tooltip">
                    <span className="error-text-span">{errorMessages.password}</span>
                  </div>
                )}
              </div>
            </InputWrapper>

            <InfoCard>
              <InfoIconCircle>
                <Email style={{ fontSize: '24px' }} />
              </InfoIconCircle>
              <InfoTextWrapper>
                <InfoTitle>Didn&apos;t receive the email?</InfoTitle>
                <InfoDescription>
                  Check your spam or junk folder, or request another verification email above.
                </InfoDescription>
              </InfoTextWrapper>
            </InfoCard>

            <ButtonWrapper>
              <TextButton onClick={handleBackToLogin} color="primary">
                Back to login
              </TextButton>
              <Button type="submit">Verify {loading && <span className="loader"></span>}</Button>
            </ButtonWrapper>
          </Form>
        </Card>
      ) : formStep === loginSteps.resetPassword ? (
        <>
          <Card>
            <Title>Reset Your Password</Title>
            <Form onSubmit={handleResetPassword}>
              <div
                className="input-container"
                style={{ display: getConfirmationCode ? 'none' : 'block' }}
              >
                <Input
                  type="email"
                  placeholder="Email"
                  name="email"
                  value={getConfirmationCode ? resetEmail : undefined}
                  onChange={(e) => !getConfirmationCode && setResetEmail(e.target.value)}
                />
                {errorMessages.email && (
                  <div className="error-tooltip">
                    <span className="error-text-span">{errorMessages.email}</span>
                  </div>
                )}
              </div>

              {getConfirmationCode ? (
                <>
                  <InputWrapper>
                    <div className="input-container">
                      <Input
                        name="password"
                        placeholder="Password"
                        type={showPassword ? 'text' : 'password'}
                      />
                      <IconButton onClick={togglePasswordVisibility}>
                        {showPassword ? <OpenEye /> : <ClosedEye />}
                      </IconButton>
                      {errorMessages.password && (
                        <div className="error-tooltip">
                          <span className="error-text-span">{errorMessages.password}</span>
                        </div>
                      )}
                    </div>
                    <TextButton style={{ marginBottom: '10px' }}>
                      Password must be at least 6 characters, contain at least 1 number and 1
                      special character (e.g. Pass1@).
                    </TextButton>
                  </InputWrapper>
                  <Input name="confirmationCode" placeholder="Confirmation Code" type="text" />
                </>
              ) : null}
              <ButtonWrapper>
                <TextButton onClick={handleBackToLogin} color="primary">
                  Want to go back to the login page? Click here.
                </TextButton>
                <Button type="submit">
                  {getConfirmationCode ? 'Reset Password' : 'Send Code'}
                  {loading && <span className="loader"></span>}
                </Button>
              </ButtonWrapper>
            </Form>
          </Card>
        </>
      ) : (
        <Card>
          <TitleRow>
            <Title>Login to Your Account</Title>
            <TextButton
              onClick={handleSignupClick}
              color="primary"
              style={{ marginBottom: '30px' }}
            >
              New user? <span style={{ textDecoration: 'underline' }}>Sign up</span>
            </TextButton>
          </TitleRow>

          <Form onSubmit={handleSubmit}>
            <div className="input-container">
              <Input type="email" placeholder="Email" name="email" />
              {errorMessages.email && (
                <div className="error-tooltip">
                  <span className="error-text-span">{errorMessages.email}</span>
                </div>
              )}
            </div>
            <InputWrapper>
              <div className="input-container">
                <Input
                  name="password"
                  placeholder="Password"
                  type={showPassword ? 'text' : 'password'}
                />
                <IconButton onClick={togglePasswordVisibility}>
                  {showPassword ? <OpenEye /> : <ClosedEye />}
                </IconButton>
                {errorMessages.password && (
                  <div className="error-tooltip">
                    <span className="error-text-span">{errorMessages.password}</span>
                  </div>
                )}
              </div>
            </InputWrapper>
            <ButtonWrapper>
              <TextButton onClick={() => setFormStep(loginSteps.resetPassword)} color="primary">
                Forgot your password? Click here to reset it.
              </TextButton>
              <Button type="submit">Login {loading && <span className="loader"></span>}</Button>
            </ButtonWrapper>
          </Form>
        </Card>
      )}

      {freeUserPopUp && (
        <FreeUserPopUpMessage
          onClose={() => setFreUserPopUp(false)}
          androidQR={androidQR}
          iosQR={iosQR}
        />
      )}
    </Container>
  );
};

export default Login;
